<template>
  <div class="gs-asset-type">
    <div class="gs-asset-split">
      <template v-for="(ad, index) in thankyouPageAds">
        <section v-if="isThankYouPageAdTabActive(ad)" :key="ad.id" class="gs-asset-editor">
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_groovesell_ad_name')">
                <text-input
                  v-model="ad.name"
                  :validator="$v.localFunnelResource.thankyou_page_ads.$each[index].name"
                  :attribute="$t('frontend_groovesell_ad_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="ad.enabled" :value="ad.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-ad"
                title="Delete ad"
                aria-label="Delete ad"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_ad') }}</span>
            <gs-rich-text-editor
              v-model="ad.body"
              :html-view.sync="isThankYouPageHTMLView"
              :variables="variableMenu"
              :upload-url="uploadUrl"
              :placeholder="$t('frontend_resources_ad')"
            />
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasThankYouPageAds && !selectedThankyouPageAd" class="gs-asset-empty">
        <p>Select an ad from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Thank-you page ads"
        :items="thankyouPageAds"
        :selected-id="selectedThankyouPageAdId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled ad"
        :empty-copy="$t('frontend_resources_you_dont_have_thankyou_page')"
        enabled-key="enabled"
        :error-ids="thankyouPageAdsErrorIds"
        @select="selectThankyouPageAd"
        @add="createThankYouPageAd()"
        @reorder="reorderThankyouPageAds"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this ad?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteThankYouPageAd()"
    />
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import GsRichTextEditor from '@/components/editor/GsRichTextEditor.vue';
import { buildVariableMenu } from '@/components/editor/editorVariables';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import { toTabOrder, sortByTabOrder } from './tabOrder';

export default {
  mixins: [generateNumberMixin],
  components: {
    AssetItemList,
    GsRichTextEditor,
    TrashIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    thankyouPageAdsTab: {
      type: String,
      default: '',
    },
    uploadUrl: {
      type: String,
      default: '',
    },
    landingPages: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      thankyouPageAdId: null,
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localThankyouPageAdsTab: this.thankyouPageAdsTab,
      isThankYouPageHTMLView: false,
    };
  },
  computed: {
    hasThankYouPageAds() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.thankyou_page_ads &&
        this.localFunnelResource.thankyou_page_ads.length
      );
    },
    thankyouPageAds() {
      return (this.localFunnelResource && this.localFunnelResource.thankyou_page_ads) || [];
    },
    selectedThankyouPageAd() {
      return this.thankyouPageAds.filter((ad) => this.isThankYouPageAdTabActive(ad))[0] || null;
    },
    selectedThankyouPageAdId() {
      if (!this.localThankyouPageAdsTab) return null;
      return this.localThankyouPageAdsTab.replace('thankyou_page_ad_tab_', '');
    },
    variableMenu() {
      // The Insert menu wears the same two labels the plain-text Insert
      // dropdowns beside it do, so both editors name the tokens identically.
      return buildVariableMenu(this.landingPages, {
        labels: {
          affiliateName: this.$t('frontend_resources_add_affiliate_name'),
          affiliateLink: this.$t('frontend_resources_add_affiliate_link'),
        },
      });
    },
    thankyouPageAdsErrorIds() {
      let thankyouPageAdsErrorIds = [];
      let thankyouPageAdsIndex;
      for (thankyouPageAdsIndex in this.$v.localFunnelResource.thankyou_page_ads.$each.$iter) {
        if (this.$v.localFunnelResource.thankyou_page_ads.$each.$iter[thankyouPageAdsIndex].$error) {
          thankyouPageAdsErrorIds.push(
            this.$v.localFunnelResource.thankyou_page_ads.$each.$iter[thankyouPageAdsIndex].$model.id
          );
        }
      }
      return thankyouPageAdsErrorIds;
    },
  },
  methods: {
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectThankyouPageAd(id) {
      this.handleThankyouPageAdsTabChange(`thankyou_page_ad_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderThankyouPageAds(ids) {
      this.handleThankyouPageTabOrderUpdate(toTabOrder('thankyou_page_ad', ids));
    },
    handleThankyouPageAdsTabChange(newTab) {
      this.localThankyouPageAdsTab = newTab || this.localThankyouPageAdsTab;
      this.$emit('handleThankyouPageAdsTabChange', this.localThankyouPageAdsTab);
    },
    handleThankyouPageTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.thankyou_page_ads, 'thankyou_page_ad', updatedTabs);

      this.updateFunnelResource();
    },
    createThankYouPageAd() {
      const newAdId = this.generateNumber();
      const newAdCount = this.localFunnelResource.thankyou_page_ads.length + 1;

      this.localFunnelResource.thankyou_page_ads.push({
        id: newAdId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_ad')} ${newAdCount}`,
        body: '',
      });

      this.localThankyouPageAdsTab = `thankyou_page_ad_tab_${newAdId}`;

      this.updateFunnelResource();
      this.handleThankyouPageAdsTabChange();
    },
    openConfirmModal(thankyouPageAdId) {
      this.thankyouPageAdId = thankyouPageAdId;
      this.confirmOpen = true;
    },
    async deleteThankYouPageAd() {
      this.localFunnelResource.thankyou_page_ads.splice(this.thankyouPageAdId, 1);

      if (this.localFunnelResource.thankyou_page_ads.length > 0) {
        let nextOpenIndex = 0;
        if (this.thankyouPageAdId == 0) {
          nextOpenIndex = 0;
        } else {
          nextOpenIndex = this.thankyouPageAdId - 1;
        }

        const nextOpenId =
          this.localFunnelResource.thankyou_page_ads[nextOpenIndex].id;
        this.localThankyouPageAdsTab = `thankyou_page_ad_tab_${nextOpenId}`;
      } else {
        this.localThankyouPageAdsTab = '';
      }

      this.updateFunnelResource();
      this.handleThankyouPageAdsTabChange();
    },
    isThankYouPageAdTabActive(ad) {
      return this.localThankyouPageAdsTab === `thankyou_page_ad_tab_${ad.id}`;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  validations: {
    localFunnelResource: {
      thankyou_page_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>
